import * as React from "react";
import { connect } from "react-redux";

import * as moment from "moment";
import { Authorize } from "reauthorize";
import { Dropdown, Container, Grid, Segment } from "semantic-ui-react";

import { Appointment } from "@teamprevent/common/calendar";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { AppointmentAndParent } from "@teamprevent/common/calendar/models";

import { EditModal as ClinicEditModal } from "../clinics/components/EditModal";
import { ApplicationState } from "../models";
import { CreateTimeNoteModal } from "../timeNotes/components/CreateTimeNoteModal";
import { BookAdHocClinicAppointmentModal } from "../../bookAppointments/components/BookAdHocClinicAppointmentModal";
import {
    ClinicHealthSurveillanceRepeatConsultationForAppointment
} from "../../healthSurveillance/components/ClinicHealthSurveillanceRepeatConsultationForAppointment";
import { ADMIN_ROLES } from "../../auth";
import { ClinicHealthSurveillanceEmployeeQuestionnaire } from "../../healthSurveillance/components/ClinicHealthSurveillanceEmployeeQuestionnaire";

import { ClinicHealthSurveillanceConsultationForAppointment } from "../../healthSurveillance/components/ClinicHealthSurveillanceConsultationForAppointment";

import { AppointmentModal } from "./AppointmentModal";
import { Calendar } from "./Calendar";

import { replace, push, Fragment, Location, Link } from "redux-little-router";

interface StateProps {
    path: string;
}

interface DispatchProps {
    updateRouteWithNewDate: (startDate: moment.Moment, restrictToAppointmentId?: string) => void;
    onAppointmentClick: (path: string, appointment: Appointment) => void;
    navigateToParentRoute: () => void;
    createTimeNoteAppointment: (path: string, time: moment.Moment) => void;
}

interface MergedProps {
    updateRouteWithNewDate: (startDate: moment.Moment, restrictToAppointmentId?: string) => void;
    onAppoinmentClick: (appointment: Appointment) => void;
    navigateToParentRoute: () => void;
    createTimeNoteAppointment: (time: moment.Moment) => void;
    path: string;
}

interface State {
    selectedAppointment?: AppointmentAndParent;
}

class ClinicsCalendarUnconnected extends React.Component<MergedProps, State> {
    public state: State = {};

    private onAppointmentClick = (appointment: AppointmentAndParent) => {
        this.setState({ selectedAppointment: appointment });
        this.props.onAppoinmentClick(appointment);
    };

    public render() {
        return (
            <>
                <Fragment forRoute="/:date/:clinicId/appointment/:appointmentId/questionnaire" withConditions={this.isAppointmentRoute}>
                    <ClinicHealthSurveillanceEmployeeQuestionnaire />
                </Fragment>
                <Fragment
                    forRoute="/:date/:clinicId/appointment/:appointmentId/consultation/:taskId/repeatMedical"
                    withConditions={this.isAppointmentRouteWithTaskId}
                >
                    <ClinicHealthSurveillanceRepeatConsultationForAppointment />
                </Fragment>
                <Fragment forRoute="/:date/:clinicId/appointment/:appointmentId/consultation" withConditions={this.isAppointmentRoute}>
                    <ClinicHealthSurveillanceConsultationForAppointment />
                </Fragment>
                <Fragment forRoute="/:date/create-time-note" >
                    <CreateTimeNoteModal />
                </Fragment>
                <Fragment forRoute="/:date/clinic/:clinicId/book-ad-hoc-clinic-appointment">
                    <BookAdHocClinicAppointmentModal
                        startTime={this.state.selectedAppointment?.start}
                        endTime={this.state.selectedAppointment?.end}
                        minStartTime={this.state.selectedAppointment?.parentAppointment?.start}
                        maxEndTime={this.state.selectedAppointment?.parentAppointment?.end}
                        adminUsers={this.state.selectedAppointment?.teamPreventUsers}
                        clinicId={this.state.selectedAppointment?.parentAppointment?.id}
                    />
                </Fragment>
                <Fragment forRoute="/">
                    <Container className="schedule-clinics-calendar">
                        <Grid columns="equal">
                            <Grid.Column>
                                <h1>Schedule Clinics</h1>
                            </Grid.Column>
                            <Grid.Column>
                                <Authorize authorize={[ADMIN_ROLES.ADMINISTRATOR.name, ADMIN_ROLES.SENIOR_ADMINISTRATOR.name, ADMIN_ROLES.SYSTEM_ADMIN.name]}>
                                    <Dropdown className="icon create-button" labeled button text="Create" icon="add" direction="left">
                                        <Dropdown.Menu className="create-clinic">
                                            <Dropdown.Item as={Link} text="Create Clinic" href={`${this.props.path}/create`} />
                                            <Dropdown.Item as={Link} text="Create Mobile Clinic" href={`${this.props.path}/create-mobile-clinic`} />
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </Authorize>
                            </Grid.Column>
                        </Grid>
                        <Segment>
                            <Calendar
                                showClinics
                                onDateChange={this.props.updateRouteWithNewDate}
                                onAppointmentClick={this.onAppointmentClick}
                                onHourClick={this.onHourClick}
                                showAppointmentCount={true}
                            />
                        </Segment>
                        <ClinicEditModal />
                        <AppointmentModal onModalClose={this.props.navigateToParentRoute} />
                    </Container>
                </Fragment>
            </>
        );
    }

    private onHourClick = (hour: moment.Moment) => {
        this.props.createTimeNoteAppointment(hour);
    };

    private isAppointmentRoute = (location: Location) => !!location.params && !!location.params.appointmentId;

    private isAppointmentRouteWithTaskId = (location: Location) => !!location.params && !!location.params.appointmentId && !!location.params.taskId;
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    path: state.router.pathname
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
    updateRouteWithNewDate: (startDate: moment.Moment, restrictToAppointmentId?: string) => {
        const queryParam = restrictToAppointmentId ? `?RestrictToAppointmentId=${restrictToAppointmentId}` : "";
        dispatch(replace(
            `/clinics/calendar/${startDate.format(moment.HTML5_FMT.DATE)}${queryParam}`));
    },
    onAppointmentClick: (path: string, appointment: AppointmentAndParent) => {
        if (appointment.ohCaseId && appointment.customer) {
            dispatch(push(`${path}/${appointment.customer.id}/${appointment.ohCaseId}/${appointment.id}`));
        }
        else if (appointment.id && !appointment.parentAppointment) {
            dispatch(push(`${path}/${appointment.id}`));
        }
        else if (!appointment.id && appointment.parentAppointment?.id) {
            dispatch(push(`${path}/clinic/${appointment.parentAppointment?.id}/book-ad-hoc-clinic-appointment`));
        }
        else if (appointment.id && appointment.parentAppointment?.id) {
            dispatch(push(`${path}/clinic/${appointment.parentAppointment?.id}/appointment/${appointment.id}`));
        }
    },
    navigateToParentRoute: () => dispatch(navigateToParentRoute()),
    createTimeNoteAppointment: (path: string, time: moment.Moment) =>
        dispatch(push({
            pathname: `${path}/create-time-note`,
            query: { time: time.format(moment.HTML5_FMT.DATETIME_LOCAL) }
        })),
});

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps): MergedProps => {
    const { onAppointmentClick, ...otherDispatchProps } = dispatchProps;
    return {
        ...otherDispatchProps,
        path: stateProps.path,
        onAppoinmentClick: (appointment) => onAppointmentClick(stateProps.path, appointment),
        createTimeNoteAppointment: (time: moment.Moment) => dispatchProps.createTimeNoteAppointment(stateProps.path, time)
    };
};

export const ClinicsCalendar = connect(mapStateToProps, mapDispatchToProps, mergeProps)(ClinicsCalendarUnconnected);
